/**
 * Thin React hook wrapper around the module-level employee cache.
 * Triggers a load on mount and delegates lookups to the shared singleton.
 */

import { useCallback, useEffect, useState } from "react";
import { useDgaApi } from "./useDgaApi";
import {
  ensureEmployeesLoaded,
  getEmployeeById as cacheGetEmployeeById,
  getEmployeeUser as cacheGetEmployeeUser,
  isLoaded as cacheIsLoaded,
} from "../cache/employeeCache";

export function useEmployeeCache(dealerId: string | undefined) {
  const { fetchEmployees } = useDgaApi();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!dealerId) return;
    setIsLoading(true);
    ensureEmployeesLoaded(dealerId, (offset, limit) =>
      fetchEmployees({ offset, limit }),
    )
      .catch(() => {
        // Errors are suppressed at the hook level — lookups will fall back to
        // "another user" via the module-level cache (which remains empty).
      })
      .finally(() => setIsLoading(false));
  }, [dealerId, fetchEmployees]);

  const getEmployeeById = useCallback(
    (userId: string) => cacheGetEmployeeById(dealerId ?? "", userId),
    [dealerId],
  );

  const getEmployeeUser = useCallback(
    (userId: string) => cacheGetEmployeeUser(dealerId ?? "", userId),
    [dealerId],
  );

  return {
    getEmployeeById,
    getEmployeeUser,
    isLoading,
    isLoaded: dealerId ? cacheIsLoaded(dealerId) : false,
  };
}
